import { useNavigate } from "@remix-run/react";
import { TROPHY_TYPE, type AchievementAwardedMessage } from "@repo/shared";
import { cva } from "class-variance-authority";
import { camelCase } from "lodash-es";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MessengerIcon, WhatsappIcon } from "~/assets/svgs";
import { Icon } from "~/components/Icon";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import { cn } from "~/lib/utils";

import type { VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";

const toastVariants = cva(
  "lg:details text-[10px] leading-[160%] flex max-w-[358px] w-full lg:max-w-[580px] items-center gap-x-3 rounded-xl px-5 py-4 text-white",
  {
    variants: {
      variant: {
        1: "bg-[#333A63]",
        2: "bg-[#75216F]",
        3: "bg-[#0648AC]",
        4: "bg-[#4E11A1]",
      },
    },
    defaultVariants: {
      variant: 1,
    },
  },
);

type GamificationToastProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof toastVariants> & {
    badge: AchievementAwardedMessage["badge"];
    onDismiss: () => void;
    isShared?: boolean;
  };

const popoverBadgeVariants = cva(
  "details flex w-max gap-x-1 items-center justify-between px-2 py-[1.5px] border rounded-lg",
  {
    variants: {
      variant: {
        1: "gamification__badge-1",
        2: "gamification__badge-2",
        3: "gamification__badge-3",
        4: "gamification__badge-4",
      },
    },
    defaultVariants: {
      variant: 1,
    },
  },
);

export const GamificationToast = ({
  badge,
  onDismiss,
  className,
  isShared,
  ...props
}: GamificationToastProps) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const navigate = useNavigate();
  const { badgeType, name, tier, description, descriptionAfterShare } = badge;
  const [showShareOptions, setShowShareOptions] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const variant = 1 <= tier && tier <= 4 ? (tier as 1 | 2 | 3 | 4) : 1;

  const badgeImage = useMemo(() => {
    return `app/assets/badges/${badge.badgeAvatarId}.png`;
  }, [badge.badgeAvatarId]);

  const handleShareClick = () => {
    setShowShareOptions(!showShareOptions);
  };

  return (
    <div className={cn(toastVariants({ variant }), className)} {...props}>
      <div className="flex w-full gap-x-2">
        <p className="max-w-[133px] md:max-w-none">
          {t(`gamification.badgeType.${camelCase(badgeType)}`)}
          <span className="font-bold">&ldquo;{name}&rdquo;</span>
        </p>
        <Popover>
          <PopoverTrigger asChild>
            <button onClick={() => console.log("clicked")} className="underline">
              {t("gamification.viewBadge")}
            </button>
          </PopoverTrigger>
          <PopoverContent
            sideOffset={-60}
            className="flex min-h-[493px] w-[calc(100vw-2rem)] max-w-[358px] flex-col items-center rounded-3xl py-6 sm:min-h-[657px] sm:w-[560px] sm:max-w-[560px] sm:px-9 lg:p-[52px]"
          >
            <div className={popoverBadgeVariants({ variant })}>
              <p>
                <span className="font-bold">{name}:</span>{" "}
                {t("gamification.level", { level: variant })}
              </p>
            </div>
            <img
              src={badgeImage}
              alt={name}
              className="mt-3 lg:mt-6"
              style={{
                width: isDesktop ? "335px" : "286px",
                height: isDesktop ? "240px" : "218px",
                objectFit: "contain",
              }}
            />
            {/* TODO: add date from backend */}
            {isShared && (
              <span className="mt-4 font-madimi">
                {new Date().toLocaleDateString("pl-PL", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}{" "}
                r.
              </span>
            )}
            <hgroup className="mt-5 text-center lg:mt-10">
              <h3 className="h5 lg:h4 text-violet-800">
                {badge.trophyType === TROPHY_TYPE.STARTER
                  ? t("gamification.goodStartTitle")
                  : t("gamification.congratulationsTitle")}
              </h3>
              <p className="body-sm sm:body-base mt-1 max-w-[334px] text-violet-800 sm:px-2 lg:max-w-[456px]">
                {isShared
                  ? descriptionAfterShare
                  : description?.split(".").map((part, index) => (
                      <span key={index} className={index === 1 ? "font-bold" : ""}>
                        {part}
                        {index === 0 ? "." : ""}
                      </span>
                    ))}
              </p>
            </hgroup>
            {!isShared && (
              <div className="mt-4 w-full max-w-[334px] lg:mt-8 lg:max-w-[456px]">
                <Button onClick={handleShareClick} className="w-full gap-x-2">
                  {t("gamification.shareAchievement")}
                  <Icon name="SHARE" className="size-4" />
                </Button>
                {showShareOptions && !isMobile && (
                  <div className="mt-4 flex w-full items-center gap-x-2">
                    <Button
                      variant="ghost"
                      className="flex h-[73px] w-full flex-col items-center justify-center gap-1 rounded-xl border-[1px] border-neutral-300"
                    >
                      <WhatsappIcon />
                      <span>WhatsApp</span>
                    </Button>
                    <Button
                      variant="ghost"
                      className="flex h-[73px] w-full flex-col items-center justify-center gap-1 rounded-xl border-[1px] border-neutral-300"
                    >
                      <MessengerIcon />
                      <span>Messenger</span>
                    </Button>
                  </div>
                )}
                <Button
                  onClick={() => navigate("/user-progress")}
                  variant="link"
                  className="mt-2 w-full lg:mt-3"
                >
                  {t("gamification.viewAllAchievements")}
                </Button>
              </div>
            )}
            {isShared && <Icon name="LOGO" className="mt-8 h-10 text-violet-800" />}
          </PopoverContent>
        </Popover>
      </div>
      <button onClick={onDismiss}>
        <Icon name="CROSS" className="size-5 text-white" />
      </button>
    </div>
  );
};
